import { ProductCollection } from "@/modules/collections/types/collection.types"
import { CheckoutTrackingContext, EventTrackingContext } from "@/utils/attribution"
import { visibleCollectionProducts } from '../utils/collectionAvailability'

const backendApiUrl: string = process.env.REACT_APP_BACKEND_API_URL!
const backendShopApiUrl: string = `${backendApiUrl.replace(/\/$/, '')}/shop`

const getShopEnvironment = (): string => {
	return (process.env.REACT_APP_ENVIRONMENT || process.env.ENVIRONMENT || 'prod').trim().toLowerCase()
}

interface CountryResponse {
	country_code: string;
}

export type InstagramLinkResolveResponse = {
	code: string
	publicLink: string
	destinationUrl: string
	destinationPath: string
	destinationQuery?: string
	attribution: {
		utmSource?: string
		utmMedium?: string
		utmCampaign?: string
		utmContent?: string
		kibuClickId?: string
		landingPath?: string
	}
}

export const resolveInstagramLink = async (code: string): Promise<InstagramLinkResolveResponse> => {
	const response = await fetch(`${backendApiUrl.replace(/\/$/, '')}/social/instagram/links/${encodeURIComponent(code)}/resolve/`, {
		method: 'GET',
		cache: 'no-store',
	})

	if (!response.ok) {
		const error = await response.json().catch(() => ({}))
		throw new Error(error.error?.message || 'Failed to resolve Instagram link')
	}

	return response.json()
}

// Cache promise to deduplicate in-flight requests
let viewerCountryPromise: Promise<string> | null = null;

export const fetchViewerCountry = async (): Promise<string> => {
	// Return existing promise if already fetching
	if (viewerCountryPromise) {
		return viewerCountryPromise;
	}

	try {
		// Create the promise and cache it
		viewerCountryPromise = (async () => {
			try {
				const response = await fetch(`${backendShopApiUrl}/viewerCountry/`, {
					method: 'GET',
					cache: 'no-store',
					headers: {
						'Cache-Control': 'no-cache',
					},
				});
				if (!response.ok) {
					throw new Error('Failed to fetch country code');
				}
				const data: CountryResponse = await response.json();
				return data.country_code.toLowerCase();
			} catch (error) {
				console.error('Failed to fetch viewer country:', error);
				return 'gb'; // Default to GB if the API fails
			}
		})();

		return await viewerCountryPromise;
	} finally {
		// Don't clear promise - keep it cached for the session
		// This prevents duplicate requests for the same region detection
	}
}

interface IApiComponent {
	componentType: string
	name: string
	defaultVariant: string
	quantity: number
	display?: Array<{
		configuratorFrontOn: boolean
		'z-index'?: number
	}>
	defaultMaterial?: string
	materialOptions?: string[]
	positions?: Array<{
		position: string
		quantity: number
	}>
}

interface ITransformedComponent {
	componentVariantId: { $oid: string }
	componentType: string
	name: string
	position: string | null
	chosenMaterial: { $oid: string }
	display?: Array<{
		configuratorFrontOn: boolean
		'z-index'?: number
	}>
	materialOptions?: string[]
}

const transformComponents = (apiComponents: IApiComponent[]): ITransformedComponent[] => {
	return apiComponents.flatMap(component => {
		// Skip components without defaultMaterial
		// if (!component.defaultMaterial && !component.materialOptions) {
		// 	return []
		// }

		const baseComponent: Omit<ITransformedComponent, 'position'> = {
			componentVariantId: {
				$oid: component.defaultVariant
			},
			componentType: component.componentType,
			name: component.name,
			display: component.display,
			materialOptions: component.materialOptions,
			chosenMaterial: {
				$oid: component.defaultMaterial || ''
			}
		}

		if (!component.positions) {
			// If no positions specified, create a single component with null position
			return [{
				...baseComponent,
				position: null,
			} as ITransformedComponent]
		}

		// Create a component for each position
		return component.positions.flatMap(pos => {
			const components: ITransformedComponent[] = []
			for (let i = 0; i < pos.quantity; i++) {
				components.push({
					...baseComponent,
					position: pos.position,
				})
			}
			return components
		})
	})
}

// Cache for product data requests to prevent duplicates
// Uses a timeout to allow deduplication of concurrent requests while not blocking fresh data
const productDataPromiseCache = new Map<string, { promise: Promise<any>; timeoutId: NodeJS.Timeout }>();
const PRODUCT_CACHE_EXPIRY_MS = 30000; // 30 seconds - enough for concurrent requests, less than localStorage 10 min cache

export const fetchProductData = async (productId: string, countryCode: string) => {
	const cacheKey = `${productId}|${countryCode.toLowerCase()}`;
	const environment = process.env.REACT_APP_ENVIRONMENT || process.env.ENVIRONMENT;

	// Return in-flight promise if already fetching this product/country combination
	if (productDataPromiseCache.has(cacheKey)) {
		return productDataPromiseCache.get(cacheKey)!.promise;
	}

	try {
		// Create and cache the promise
		const promise = (async () => {
			const baseUrl = `${backendApiUrl.replace(/\/$/, '')}/shop/products/${productId}/`;
			const queryParams = new URLSearchParams();
			if (environment && environment !== 'prod') {
				queryParams.set('testMode', environment);
			}
			const requestUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
			const response = await fetch(requestUrl, {
				method: 'GET',
				cache: 'no-store',
				headers: {
					'Content-Type': 'application/json',
					'Cache-Control': 'no-cache',
					'x-country-code': countryCode.toUpperCase()
				},
			})
			if (!response.ok) {
				throw new Error(response.status === 404
					? 'This product is currently unavailable.'
					: 'Could not load this product. Please try again.')
			}
			const data = await response.json()
			if (!data?.product || typeof data.product !== 'object' || !data.product._id) {
				throw new Error('Could not load this product. Please try again.')
			}
			// console.log('[data]: ', data)
			// console.log('[data] backendApiUrl: ', backendApiUrl)
			// console.log('[data] productId: ', productId)
			// console.log('[data] countryCode: ', countryCode.toUpperCase())

			// Transform components in the main product and all options
			if (data.product.availableOptions) {
				data.product.availableOptions.forEach((option: any) => {
					option.options.forEach((product: any) => {
						if (product.components) {
							product.components = transformComponents(product.components)
						}
					})
				})
			}

			return data
		})();

		// Set up automatic cache expiry after a reasonable time window
		// This allows deduplication of concurrent requests while not blocking fresh data
		const timeoutId = setTimeout(() => {
			productDataPromiseCache.delete(cacheKey);
		}, PRODUCT_CACHE_EXPIRY_MS);

		productDataPromiseCache.set(cacheKey, { promise, timeoutId });

		const result = await promise;
		// Test edits should not reuse a completed catalogue response on revisits.
		if ((environment || '').trim().toLowerCase() === 'test' && productDataPromiseCache.get(cacheKey)?.promise === promise) {
			clearTimeout(timeoutId);
			productDataPromiseCache.delete(cacheKey);
		}
		// Production retains its existing request reuse window.
		return result;
	} catch (error) {
		const cached = productDataPromiseCache.get(cacheKey);
		if (cached) {
			clearTimeout(cached.timeoutId);
			productDataPromiseCache.delete(cacheKey); // Clear failed request immediately
		}
		console.error('Failed to fetch product data:', error)
		throw new Error(error instanceof Error && error.message === 'This product is currently unavailable.'
			? error.message
			: 'Could not load this product. Please try again.')
	}
}

export interface StockCheckCalculatedStock {
	stockStatus?: string
	isPreorder?: boolean
	canPreorder?: boolean
	remainingStock?: number | null
	availableForSale?: boolean
	stockMessage?: string | null
	outOfStockSince?: string | null
	maxDelayDays?: number | null
	estimatedDispatchDays?: {
		min?: number
		max?: number
		unit?: string
	} | null
	limitingComponent?: {
		componentVariantId: string
		name: string
		available: number
		category: string
	}
	sharedPool?: {
		totalAvailable?: number | null
		siblingProductItemIds?: string[]
	} | null
}

export interface StockCheckItem {
	productItemId: string
	calculatedStock: StockCheckCalculatedStock
}

export interface HolidayInfo {
	active: boolean
	message: string
	endDate: string
	remainingDays: number
}

export interface StockCheckResponse {
	items: StockCheckItem[]
	missingIds?: string[]
	globalProductNotice?: string | null
	holidayInfo?: HolidayInfo | null
}

export type KibuCareTier = {
	minQty: number
	maxQty?: number
	amount: number
	currency: string
}

export type KibuCarePlanPricing = {
	tiers: KibuCareTier[]
}

export type KibuCarePlan = {
	_id: string
	optionTitle: string
	productName: string
	productCatalogueId: string
	heroImage?: string
	images?: Array<
		| string
		| {
			url?: string
			thumbnailUrl?: string
			alt?: string
			order?: number
		}
	>
}

export type KibuCareProductPricingEntry = {
	productCatalogueId: string
	productName: string
	prices: Record<string, KibuCarePlanPricing>
}

export type KibuCareData = {
	plans: KibuCarePlan[]
	productPricing: KibuCareProductPricingEntry[]
}

const stockCheckPromiseCache = new Map<string, { promise: Promise<StockCheckResponse>; timeoutId: NodeJS.Timeout }>()
const STOCK_CHECK_CACHE_EXPIRY_MS = 30000

const getStockCheckCacheKey = (params: {
	productCatalogue: string[]
	environment?: string
}): string => {
	const normalizedCatalogue = Array.from(new Set(params.productCatalogue)).sort().join(',')
	return [
		normalizedCatalogue,
		params.environment || 'prod',
	].join('|')
}

type StockCheckRequestParams = {
	productCatalogue?: string[]
	bypassCache?: boolean
	stockBypassToken?: string
}

export const fetchKibuCarePlans = async (countryCode: string): Promise<KibuCareData> => {
	const safeCountryCode = (countryCode || 'GB').toUpperCase()
	const environment = process.env.REACT_APP_ENVIRONMENT || process.env.ENVIRONMENT
	const params = new URLSearchParams()

	if (environment && environment !== 'prod') {
		params.set('testMode', 'test')
	}

	const baseUrl = backendApiUrl.replace(/\/$/, '')
	const requestUrl = `${baseUrl}/shop/kibucare/${params.toString() ? `?${params.toString()}` : ''}`

	try {
		const response = await fetch(requestUrl, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				'X-Country-Code': safeCountryCode
			}
		})

		if (!response.ok) {
			return { plans: [], productPricing: [] }
		}

		const payload = await response.json()
		if (payload?.status !== 'success' || !payload?.data) {
			return { plans: [], productPricing: [] }
		}

		return {
			plans: Array.isArray(payload.data.plans) ? payload.data.plans : [],
			productPricing: Array.isArray(payload.data.productPricing) ? payload.data.productPricing : []
		}
	} catch (error) {
		console.warn('Failed to fetch Kibu Care plans', error)
		return { plans: [], productPricing: [] }
	}
}

export const fetchStockCheck = async (request: StockCheckRequestParams | string[]): Promise<StockCheckResponse> => {
	const normalizedRequest: StockCheckRequestParams = Array.isArray(request)
		? { productCatalogue: request }
		: request

	const productCatalogue = (normalizedRequest.productCatalogue || []).filter(Boolean)
	const bypassCache = normalizedRequest.bypassCache === true
	const stockBypassToken = normalizedRequest.stockBypassToken?.trim()

	if (!productCatalogue.length) {
		return { items: [], missingIds: [] }
	}

	const environment = process.env.REACT_APP_ENVIRONMENT || process.env.ENVIRONMENT;

	if (!bypassCache) {
		const cacheKey = getStockCheckCacheKey({ productCatalogue, environment })
		if (stockCheckPromiseCache.has(cacheKey)) {
			return stockCheckPromiseCache.get(cacheKey)!.promise
		}

		const promise = (async () => {
			const params = new URLSearchParams()
			if (productCatalogue.length) {
				params.set('productCatalogue', productCatalogue.join(','))
			}
			if (environment && environment !== 'prod') {
				params.set('testMode', 'test');
			}
				const baseUrl = backendApiUrl.replace(/\/$/, '')
				const response = await fetch(`${baseUrl}/shop/stock-check/?${params.toString()}`, {
					method: 'GET',
					cache: 'no-store',
					headers: {
						'Content-Type': 'application/json',
					},
				})

			if (!response.ok) {
				throw new Error(`Failed to fetch stock check (${response.status})`)
			}

			return response.json()
		})()

		const timeoutId = setTimeout(() => {
			stockCheckPromiseCache.delete(cacheKey)
		}, STOCK_CHECK_CACHE_EXPIRY_MS)

		stockCheckPromiseCache.set(cacheKey, { promise, timeoutId })

		try {
			return await promise
		} catch (error) {
			const cached = stockCheckPromiseCache.get(cacheKey)
			if (cached) {
				clearTimeout(cached.timeoutId)
				stockCheckPromiseCache.delete(cacheKey)
			}
			throw error
		}
	}

	const params = new URLSearchParams()
	if (productCatalogue.length) {
		params.set('productCatalogue', productCatalogue.join(','))
	}
	if (bypassCache) {
		params.set('_cb', `${Date.now()}`)
	}
	if (environment && environment !== 'prod') {
		params.set('testMode', 'test');
	}
	const baseUrl = backendApiUrl.replace(/\/$/, '')
	const response = await fetch(`${baseUrl}/shop/stock-check/?${params.toString()}`, {
		method: 'GET',
		cache: bypassCache ? 'no-store' : 'default',
		headers: {
			'Content-Type': 'application/json',
			...(bypassCache ? {
				'X-Cache-Bypass': stockBypassToken || '1',
			} : {}),
			...(bypassCache ? {
				'Cache-Control': 'no-cache, no-store, must-revalidate',
				'Pragma': 'no-cache',
				'Expires': '0',
			} : {})
		},
	})

	if (!response.ok) {
		throw new Error(`Failed to fetch stock check (${response.status})`)
	}

	return response.json()
}

export type StripeCheckoutItem = {
	productItemId: string
	quantity: number
	parentProductItemId?: string | null
	components?: Array<{
		partId: {
			$oid: string
		}
		side?: string | undefined
		chosenMaterial: {
			$oid: string
		}
	}>
	componentModifications?: {
		engravingText: string
	}
}

export type StripeCheckoutData = {
	items: StripeCheckoutItem[]
	orderNumber?: string
	shareContextToken?: string
	countryCode: string
	discountCode?: string
	discounts?: Array<{ coupon?: string; promotion_code?: string }>
	allowPromotionCodes?: boolean
	customerEmail?: string
	customerName?: string
	marketingOptOut?: boolean
	tracking?: CheckoutTrackingContext
}

type ApiErrorPayload = {
	code?: string
	message?: string
	details?: any
}

export type StockHoldInsufficientItem = {
	productItemIds?: string[]
	productNames?: string[]
	productCatalogueId?: string
	totalQuantityRequested?: number
	quantityAvailable?: number
}

export class CheckoutApiError extends Error {
	code?: string
	details?: any

	constructor(message: string, code?: string, details?: any) {
		super(message)
		this.name = 'CheckoutApiError'
		this.code = code
		this.details = details
	}
}

type ApiResponse<T> = {
	status: 'success' | 'error'
	data?: T
	error?: ApiErrorPayload
}

export interface CheckoutResponse {
	checkoutSessionId: string
	checkoutUrl: string
	orderNumber: string
	stripeMode: string
	products: any[]
	pricing: {
		subtotal: number
		tax: number
		currency: string
	}
	coupons: {
		allowPromotionCodes: boolean
		appliedDiscounts: any[]
	}
}

export type PreorderConfirmationItem = {
	productItemId: string
	productName: string
	stockMessage: string | null
}

export type CheckoutRequiresActionItem = {
	productItemId: string
	productName: string
}

export type CheckoutRequiresActionData = {
	outOfStockItems: CheckoutRequiresActionItem[]
	preorderItems: PreorderConfirmationItem[]
	canProceedWithConfirmation: boolean
	stockBypassToken?: string
}

export type CheckoutCreateSessionResult =
	| {
		status: 'success'
		data: CheckoutResponse
	}
	| {
		status: 'checkout_requires_action'
		data: CheckoutRequiresActionData
	}

export type ShopServerEventName =
	| 'PageView'
	| 'ViewContent'
	| 'AddToCart'
	| 'RemoveFromCart'
	| 'InitiateCheckout'
	| 'page_view'
	| 'view_product'
	| 'add_to_cart'
	| 'begin_checkout'
	| 'checkout_step'
	| 'purchase'

export type ShopServerEventPayload = EventTrackingContext & {
	eventName: ShopServerEventName
	actionSource?: 'website'
	value?: number
	currency?: string
	orderId?: string
	numItems?: number
	contentIds?: string[]
	contents?: Array<{
		id: string
		quantity?: number
		item_price?: number
	}>
	country?: string
	customer?: {
		email?: string
		phone?: string
		name?: string
		firstName?: string
		lastName?: string
		city?: string
		state?: string
		postalCode?: string
		country?: string
	}
}

export const sendShopServerEvent = async (payload: ShopServerEventPayload): Promise<void> => {
	try {
		const environment = getShopEnvironment()
		const params = new URLSearchParams()
		if (environment !== 'prod') {
			params.set('testMode', 'true')
		}
		const requestUrl = `${backendApiUrl}/payments/monitor/events/${params.toString() ? `?${params.toString()}` : ''}`

		await fetch(requestUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})
	} catch (error) {
		console.warn('[Meta CAPI] Failed to send server event:', payload.eventName, error)
	}
}

export const sendMetaServerEvent = sendShopServerEvent

type JourneyPopupTriggerEvent =
	| 'visit_site'
	| 'page_view'
	| 'product_view'
	| 'add_to_cart'
	| 'exit_intent'
	| 'scroll_depth'
	| 'time_on_site'
	| 'manual'

export type PopupEligibilityPayload = {
	path: string
	triggerEvent?: JourneyPopupTriggerEvent
	stripeMode?: 'test' | 'prod'
	sessionId?: string
	anonymousId?: string
	countryCode?: string
	email?: string
	customerId?: string
	knownSubscriber?: boolean
	visitorStatus?: 'new' | 'returning' | 'unknown'
	capturedOfferId?: string
	device?: 'desktop' | 'mobile'
}

export type PopupEligibilityResponse = {
	experimentToken?: string
	eligible: boolean
	reason?: string
	decisionId?: string
	flowId?: string
	flowNodeId?: string
	popupVariantId?: string
	offerId?: string
	display?: Record<string, any>
	popup?: {
		id?: string
		snapshot?: Record<string, any>
	}
	tracking?: {
		impressionEventName?: string
	}
	evaluatedAt?: string
}

export type PopupRuntimeEventName =
	| 'popup_eligible'
	| 'popup_shown'
	| 'popup_dismissed'
	| 'popup_reopened'
	| 'popup_submitted'

export type PopupRuntimeEventPayload = EventTrackingContext & {
	experimentToken?: string
	eventName: PopupRuntimeEventName
	decisionId?: string
	flowId?: string
	flowNodeId?: string
	popupVariantId?: string
	offerId?: string
	email?: string
	customerId?: string
	metadata?: Record<string, unknown>
}

export const getJourneyPopupEligibility = async (
	payload: PopupEligibilityPayload
): Promise<PopupEligibilityResponse> => {
	const environment = getShopEnvironment()
	const params = new URLSearchParams()
	const stripeMode = environment !== 'prod' ? 'test' : 'prod'
	if (environment !== 'prod') {
		params.set('testMode', 'true')
	}
	const requestUrl = `${backendApiUrl}/journeys/popup-eligibility/${params.toString() ? `?${params.toString()}` : ''}`
	const response = await fetch(requestUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			...payload,
			stripeMode: payload.stripeMode || stripeMode,
			experimentProtocol: 1,
		}),
	})
	if (!response.ok) {
		throw new Error('Failed to check popup eligibility')
	}
	return response.json()
}

export const sendJourneyPopupEvent = async (
	payload: PopupRuntimeEventPayload
): Promise<void> => {
	try {
		const environment = getShopEnvironment()
		const params = new URLSearchParams()
		if (environment !== 'prod') {
			params.set('testMode', 'true')
		}
		const requestUrl = `${backendApiUrl}/journeys/popup-events/${params.toString() ? `?${params.toString()}` : ''}`
		await fetch(requestUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		})
	} catch (error) {
		console.warn('[Journeys] Failed to send popup runtime event:', payload.eventName, error)
	}
}

export const getStripeCheckoutLink = async (
	checkoutData: StripeCheckoutData,
	options?: { confirmPreorder?: boolean }
): Promise<CheckoutCreateSessionResult> => {
	try {
		const environment = getShopEnvironment()
		const requestData: StripeCheckoutData = {
			...checkoutData
		}
		if (requestData.discounts && requestData.discounts.length > 0 && 'allowPromotionCodes' in requestData) {
			delete requestData.allowPromotionCodes
		}

		console.log('checkoutDataSent: ', JSON.stringify(requestData, null, 2))

		const params = new URLSearchParams()
		if (environment !== 'prod') {
			params.set('testMode', 'true')
		}
		const requestUrl = `${backendApiUrl}/payments/checkout/create-session/${params.toString() ? `?${params.toString()}` : ''}`

		const response = await fetch(requestUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(options?.confirmPreorder ? { 'X-Confirm-Preorder': 'true' } : {}),
			},
			body: JSON.stringify(requestData),
		})

		const responseData = await response.json()
		console.log('responseData: ', responseData)

		if (!response.ok) {
			const errorMessage = responseData?.error?.message || `Failed to fetch checkout link: ${response.statusText}`
			throw new CheckoutApiError(errorMessage, responseData?.error?.code, responseData?.error?.details)
		}

		if (responseData.status === 'checkout_requires_action') {
			return {
				status: 'checkout_requires_action',
				data: responseData.data as CheckoutRequiresActionData
			}
		}

		// Backward compatibility for the previous preorder-only response shape.
		if (responseData.status === 'preorder_confirmation_required') {
			return {
				status: 'checkout_requires_action',
				data: {
					outOfStockItems: [],
					preorderItems: responseData.data?.preorderItems || [],
					canProceedWithConfirmation: true
				}
			}
		}

		if (responseData.status === 'success') {
			const data: CheckoutResponse = responseData.data
			return {
				status: 'success',
				data
			}
		}

		if (responseData.status === 'error') {
			throw new CheckoutApiError(
				responseData.error?.message || 'Checkout request failed',
				responseData.error?.code,
				responseData.error?.details
			)
		}

		throw new Error('Unexpected checkout response format')
	} catch (error) {
		console.error('Failed to fetch checkout link:', error)
		throw error
	}
}

type ResumePreviewItem = {
	productItemId: string
	productCatalogueId?: string
	productName: string
	quantity: number
	parentProductItemId: string | null
	componentModifications?: {
		engravingText?: string
	}
	unitPrice: number
	currency: string
	stockStatus: string
	remainingStock: number | null
	stockMessage: string | null
}

type ResumePreviewData = {
	orderNumber: string
	items: ResumePreviewItem[]
	pricing: {
		subtotal: number
		tax: number
		currency: string
	}
}

type ShareCheckoutBootstrapData = {
	orderNumber: string
	customerPrefill: {
		name: string
		email: string
	}
	countryCode: string
	items: ResumePreviewItem[]
	pricing?: {
		subtotal: number
		tax: number
		currency: string
	}
	shareContextToken: string
}

type ResumeCheckoutData = {
	checkoutSessionId: string
	checkoutUrl: string
	orderNumber: string
}

const parseApiResponse = async <T,>(response: Response): Promise<T> => {
	const payload: ApiResponse<T> = await response.json()
	if (!response.ok || payload.status === 'error') {
		const error = new Error(payload.error?.message || 'Request failed') as Error & { code?: string }
		if (payload.error?.code) {
			error.code = payload.error.code
		}
		throw error
	}
	if (!payload.data) {
		throw new Error('Missing response data')
	}
	return payload.data
}

export const getResumePreview = async (orderNumber: string, token: string): Promise<ResumePreviewData> => {
	const response = await fetch(`${backendApiUrl}/payments/checkout/resume-preview/`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ orderNumber, token }),
	})

	return parseApiResponse<ResumePreviewData>(response)
}

export const resumeCheckoutSession = async (orderNumber: string, token: string): Promise<ResumeCheckoutData> => {
	const response = await fetch(`${backendApiUrl}/payments/checkout/resume/`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ orderNumber, token }),
	})

	return parseApiResponse<ResumeCheckoutData>(response)
}

export const getShareCheckoutBootstrap = async (code: string): Promise<ShareCheckoutBootstrapData> => {
	const response = await fetch(`${backendApiUrl}/payments/checkout/share/${encodeURIComponent(code)}/`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	})

	return parseApiResponse<ShareCheckoutBootstrapData>(response)
}

export const unsubscribeAbandonedEmail = async (token: string): Promise<{ status: string; message: string }> => {
	const response = await fetch(`${backendApiUrl}/emails/abandoned/unsubscribe/?token=${encodeURIComponent(token)}`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	})

	const payload = await response.json()
	if (!response.ok || payload.status === 'error') {
		throw new Error(payload.message || 'Request failed')
	}
	return payload
}

type AbandonedResubscribeResponse = {
	status: 'success' | 'error'
	message?: string
	error?: {
		code?: string
		message?: string
	}
}

export const resubscribeAbandonedEmail = async (token: string): Promise<{ status: 'success'; message?: string }> => {
	const response = await fetch(`${backendApiUrl}/emails/abandoned/resubscribe/?format=json`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ token }),
	})

	const payload: AbandonedResubscribeResponse = await response.json()
	if (!response.ok || payload.status === 'error') {
		throw new Error(payload.error?.message || payload.message || 'Unable to update your email preference.')
	}

	return {
		status: 'success',
		message: payload.message,
	}
}

export const resubscribeMarketingEmail = async (token: string): Promise<{ status: 'success'; message?: string }> => {
	const response = await fetch(`${backendApiUrl}/emails/resubscribe/?format=json`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ token }),
	})

	const payload: AbandonedResubscribeResponse = await response.json()
	if (!response.ok || payload.status === 'error') {
		throw new Error(payload.error?.message || payload.message || 'Unable to update your email preference.')
	}

	return {
		status: 'success',
		message: payload.message,
	}
}

export const unsubscribeMarketingEmail = async (token: string): Promise<{ status: 'success'; message?: string }> => {
	const response = await fetch(`${backendApiUrl}/emails/unsubscribe/?format=json`, {
		method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }),
	})
	const data = await response.json()
	if (!response.ok) throw new Error(data?.error?.message || 'Unable to unsubscribe right now.')
	return data
}

const productCollectionPromiseCache = new Map<string, { promise: Promise<ProductCollection>; timeoutId: NodeJS.Timeout }>()
const PRODUCT_COLLECTION_CACHE_EXPIRY_MS = 60 * 1000

const getProductCollectionCacheKey = (params: { id?: string; collectionCode?: string; country_code: string }): string => {
	return [
		(params.country_code || '').toLowerCase(),
		params.id || '',
		params.collectionCode || '',
	].join('|')
}

export const fetchProductCollection = async (params: { id?: string; collectionCode?: string; country_code: string }): Promise<ProductCollection> => {
	const cacheKey = getProductCollectionCacheKey(params)
	if (productCollectionPromiseCache.has(cacheKey)) {
		return productCollectionPromiseCache.get(cacheKey)!.promise
	}

	try {
		const promise = (async () => {
			const headers: Record<string, string> = {
				'Content-Type': 'application/json',
				'x-country-code': params.country_code.toUpperCase(),
			}

			if (params.id) {
				headers['x-id'] = params.id
			}

			if (params.collectionCode) {
				headers['x-collection-code'] = params.collectionCode
			}

			const response = await fetch(`${backendApiUrl}/shop/productCollection/`, {
				method: 'GET',
				cache: 'no-store',
				headers,
			})

			if (!response.ok) {
				throw new Error('Failed to fetch product collection')
			}

			const collection: ProductCollection = await response.json()
			const ids = Array.from(new Set(collection.products.map(product => product.productItemId)))
			const current: Record<string, StockCheckCalculatedStock> = {}
			// Collections can be cached at the edge. Validate their cards against
			// the same current commercial availability used by product pages.
			for (let offset = 0; offset < ids.length; offset += 100) {
				const query = new URLSearchParams({ productItems: ids.slice(offset, offset + 100).join(',') })
				const stockResponse = await fetch(`${backendShopApiUrl}/stock-check/?${query}`, { cache: 'no-store' })
				if (!stockResponse.ok) throw new Error('Could not verify collection availability')
				const stock: StockCheckResponse = await stockResponse.json()
				stock.items.forEach(item => { current[String(item.productItemId)] = item.calculatedStock || {} })
			}
			return { ...collection, products: visibleCollectionProducts(collection.products, current) }
		})()

		const timeoutId = setTimeout(() => {
			productCollectionPromiseCache.delete(cacheKey)
		}, PRODUCT_COLLECTION_CACHE_EXPIRY_MS)

		productCollectionPromiseCache.set(cacheKey, { promise, timeoutId })
		return await promise
	} catch (error) {
		const cached = productCollectionPromiseCache.get(cacheKey)
		if (cached) {
			clearTimeout(cached.timeoutId)
			productCollectionPromiseCache.delete(cacheKey)
		}
		console.error('Failed to fetch product collection:', error)
		throw error
	}
}

export const prefetchProductCollection = async (params: { id?: string; collectionCode?: string; country_code: string }): Promise<void> => {
	try {
		await fetchProductCollection(params)
	} catch (error) {
		console.error('Failed to prefetch product collection:', error)
	}
}

export interface CountryDetails {
	countryName: string;
	currency: {
		code: string;
		symbol: string;
		name: string;
	};
	returns: {
		active: boolean;
		returnDays: number;
		updateDate: string;
	} | null;
	countryFlag: string;
	shippingRates: Array<{
		name: string;
		type: string;
		thresholds?: Array<{
			minAmount: number;
			amount: number;
			displayName: string;
		}>;
		amount?: number;
		displayName?: string;
		deliveryEstimate: {
			minimum: { unit: string; value: number };
			maximum: { unit: string; value: number };
		};
	}>;
	prePaidTaxesAndDuties: any;
	dutiesHandlingFee?: { amount: number; label: string } | null;
	deliveryEstimates: Array<{
		minimum: { unit: string; value: number };
		maximum: { unit: string; value: number };
	}>;
	freeDeliveryThreshold: number;
	freeDeliveryThresholdFormatted: string;
}

// Design mockup only: the backend does not send these values yet, so Stripe still charges the old amounts.
// Custom error for unsupported regions
export class UnsupportedRegionError extends Error {
	countryCode: string;
	statusCode: number;

	constructor(countryCode: string, statusCode: number, message?: string) {
		super(message || `Region ${countryCode} is not supported`);
		this.name = 'UnsupportedRegionError';
		this.countryCode = countryCode;
		this.statusCode = statusCode;
	}
}

export const fetchCountryDetails = async (countryCode: string): Promise<CountryDetails> => {
	try {
		const response = await fetch(`${backendShopApiUrl}/countryDetails/`, {
			method: 'GET',
			cache: 'no-store',
			headers: {
				'Content-Type': 'application/json',
				'Cache-Control': 'no-cache',
				'x-country-code': countryCode.toUpperCase()
			},
		});

		if (!response.ok) {
			// 404 means unsupported region
			if (response.status === 404) {
				throw new UnsupportedRegionError(countryCode, 404, `Region ${countryCode.toUpperCase()} is not supported`);
			}
			throw new Error(`Failed to fetch country details (${response.status})`);
		}

		const data: CountryDetails = await response.json();
		return data;
	} catch (error) {
		console.error('Failed to fetch country details:', error);
		throw error;
	}
};

export interface CountryInfo {
	name: string;
	flag: string;
}

export interface CountryGroup {
	type: 'group';
	countries: {
		[key: string]: CountryInfo;
	};
}

export interface CountriesResponse {
	[key: string]: CountryInfo | CountryGroup;
}

export const fetchCountries = async (): Promise<CountriesResponse> => {
	try {
		const response = await fetch(`${backendShopApiUrl}/countries/`, {
			method: 'GET',
			cache: 'no-store',
			headers: {
				'Cache-Control': 'no-cache',
			},
		});
		if (!response.ok) {
			throw new Error('Failed to fetch countries');
		}
		const data: CountriesResponse = await response.json();
		return data;
	} catch (error) {
		console.error('Failed to fetch countries:', error);
		throw error;
	}
};

export const recogniseCheckoutEmailSubscriber = async (email: string, clickId: string, signal: AbortSignal): Promise<boolean> => {
 const response = await fetch(`${backendApiUrl.replace(/\/$/, '')}/emails/checkout/marketing-recognition/`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, cache: 'no-store', signal,
  body: JSON.stringify({ email, clickId }),
 })
 if (!response.ok) return false
 const data = await response.json()
 return data.recognised === true
}

export interface DeliveryMoney {
	amount: number
	formatted: string
}

export interface DeliveryDays {
	min: number
	max: number
}

/** Countries whose delivery facts are identical, e.g. most of Europe. */
export interface DeliveryRegion {
	label: string
	group: string
	currency: string
	countries: { code: string; name: string; flag: string }[]
	standard: { price: DeliveryMoney; freeOver: DeliveryMoney | null; deliveryDays: DeliveryDays | null } | null
	options: { label: string; price: DeliveryMoney; deliveryDays: DeliveryDays | null }[]
	duties: 'domestic' | 'prepaid' | 'duties_paid' | 'on_arrival'
	dutiesHandlingFee: (DeliveryMoney & { label: string }) | null
	upgrades: { name: string; deliveryDays: DeliveryDays }[]
}

export interface DeliveryPolicy {
	updatedAt: string | null
	countries: { code: string; name: string; flag: string; group: string }[]
	regions: DeliveryRegion[]
}

/** Every country's live delivery facts, from the same settings checkout uses. */
export const fetchDeliveryPolicy = async (): Promise<DeliveryPolicy> => {
	const response = await fetch(`${backendShopApiUrl}/delivery-policy/`, { method: 'GET' })
	if (!response.ok) {
		throw new Error('Failed to fetch delivery policy')
	}
	return response.json()
}
